import { ColumnDef } from "@tanstack/react-table";
import { AxiosError } from "axios";
import React, { useEffect, useState, useRef } from "react";

import DataTable2 from "shared/DataTable2";
import { useServerPaginatedList } from "../../shared/useServerPaginatedList";
import {
  ServerDeletedFilterValue,
  ServerStatusFilterValue,
} from "../../shared/ServerListFilters";
import ServerStatusPills from "../../shared/ServerStatusPills";
import ServerDeletedPills from "../../shared/ServerDeletedPills";

import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Option } from "../../interface/Option";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/Label";
import { Toaster } from "../../components/ui/toaster";
import { useToast } from "../../hooks/use-toast";
import { doctor, Result } from "../../interface/Doctor";
import { DoctorAxiosInstance } from "../../Network/DoctorAxiosInstance";
import { LoginAxiosInstance } from "../../Network/LoginAxiosInstance";
import SpecialtySelector from "../Shared/Specialty";
import FileUpload from "Pages/uploadFile/Doctor/Doctor";

// ─── Role helpers ─────────────────────────────────────────────────────────────
const getUserRole = (): string => {
  try {
    const token = localStorage.getItem("token") || "";
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.userType || "";
  } catch { return ""; }
};

const Doctor: React.FC = () => {
  // ✅ Role-based access
  const userRole = getUserRole();
  const canManageDeleted = userRole === "system_admin" || userRole === "super_super_admin";

  // Main data state
  const [items, setItems] = useState<doctor>({ result: [] });

  // Form state
  const [Name, setName] = useState<string>("");
  const [doctorId, setDoctorId] = useState<string>("");
  const [specialty, setSpecialty] = useState<Option[]>([]);
  const [isActive, setIsActive] = useState<boolean>(true);
  const [clearSpecialty, setClearSpecialty] = useState(false);

  // UI control states
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editIndex, setEditIndex] = useState<null | number>(null);

  // ✅ Ref to the Add/Edit DialogContent, used as the react-select menu portal target
  const [dialogContentEl, setDialogContentEl] = useState<HTMLDivElement | null>(null);

  const token = localStorage.getItem("token") || "";
  const { toast } = useToast();

  const [selectedItem, setSelectedItem] = useState<Result | null>(null);
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const [usersMap, setUsersMap] = useState<Record<number, string>>({});

  // For specialty modal
  const [specialtyDialogOpen, setSpecialtyDialogOpen] = useState(false);
  const [selectedSpecialties, setSelectedSpecialties] = useState<any[]>([]);
  const [filterName, setFilterName] = useState("");
  const [filterStatus, setFilterStatus] = useState<ServerStatusFilterValue>("");
  const [filterDeleted, setFilterDeleted] = useState<ServerDeletedFilterValue>("false");

  // Validation states
  const [nameError, setNameError] = useState<string>("");
  const [idError, setIdError] = useState<string>("");
  const [specialtyError, setSpecialtyError] = useState<string>("");

  // ─── Force Dialog state ───────────────────────────────────────────────────
  const [forceDialogOpen, setForceDialogOpen] = useState(false);
  const [forceAction, setForceAction] = useState<"softDelete" | "hardDelete" | "status" | null>(null);
  const [forceId, setForceId] = useState<string | number | null>(null);
  const [forceTargetItem, setForceTargetItem] = useState<Result | null>(null);
  const [forceError, setForceError] = useState<string>("");
  const [showAllUpdates, setShowAllUpdates] = useState(false);

  const normalizeData = (data: Result[]) => {
    return data.map((item) => ({
      ...item,
      status: item.status ? "Active" : "Inactive",
      specialtySearchText: Array.isArray(item.specialty)
        ? item.specialty.map((s) => s.name).join(", ")
        : "",
    }));
  };

  const resolveUserName = (val: any): string => {
    if (val == null) return "—";
    if (typeof val === "object") return val.name ?? val.username ?? "—";
    if (typeof val === "string" && isNaN(Number(val))) return val;
    return usersMap[Number(val)] ?? String(val);
  };

  const fetchUsers = async () => {
    try {
      const res = await LoginAxiosInstance.get("/");
      const raw: any[] = res.data?.users ?? res.data?.user ?? res.data ?? [];
      const map: Record<number, string> = {};
      raw.forEach((u: any) => {
        if (u.id != null) map[Number(u.id)] = u.name ?? u.username ?? String(u.id);
      });
      setUsersMap(map);
    } catch {}
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setName(value);
    if (!value.trim()) { setNameError("Name required"); return; }
    if (value.trim().length < 3) { setNameError("Name must be at least 3 characters long"); return; }
    const nameRegex = /^[A-Z][a-zA-Z\s]*$/;
    if (!nameRegex.test(value)) { setNameError("Invalid name. Only letters are allowed and must start with a capital letter."); return; }
    setNameError("");
  };

  const existingIds = items.result.map((item) => item.doctor_id);

  const handleIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDoctorId(value);
    if (!value.trim()) { setIdError("Doctor ID required"); return; }
    const alphanumericRegex = /^[a-zA-Z0-9]+$/;
    if (!alphanumericRegex.test(value.trim())) { setIdError("Doctor ID must contain only letters and numbers"); return; }
    if (existingIds.includes(value.trim())) { setIdError("This ID already exists"); return; }
    setIdError("");
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const clear = () => {
    setName("");
    setDoctorId("");
    setSpecialty([]);
    setIsActive(true);
    setNameError("");
    setIdError("");
    setSpecialtyError("");
  };

  const handleAddClick = () => { setEditIndex(null); clear(); setIsDialogOpen(true); };
  const handleEditClick = (item: Result) => { handleEdit(item); setIsDialogOpen(true); };

  const handleSpecialtyChange = (specialties: Option[]) => {
    setSpecialtyError("");
    if (specialties.length > 1) {
      setSpecialty([specialties[specialties.length - 1]]);
      setSpecialtyError("A doctor can only be assigned one specialty.");
      toast({ title: "Warning", description: "A doctor can only have one specialty. Only the last selected specialty was kept.", variant: "destructive" });
      return;
    }
    setSpecialty(specialties);
  };

  // ✅ fetchItems — أحدث أول + محذوفين آخر
  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    totalCount,
    allowedPageSizes,
    isTableLoading,
    manualPagination,
    fetchItems,
  } = useServerPaginatedList<Result, {
    name: string;
    active: ServerStatusFilterValue;
    deleted: ServerDeletedFilterValue;
  }>({
    filters: { name: filterName, active: filterStatus, deleted: filterDeleted },
    fetchPage: ({ page, limit, filters }) =>
      DoctorAxiosInstance.get("getDoctor", {
        params: {
          page,
          limit,
          name: filters.name.trim() || undefined,
          specialty: filters.name.trim() || undefined,
          active: filters.active || undefined,
          is_deleted: filters.deleted,
        },
      }),
    setRows: (rows) => setItems({ result: rows }),
    normalizeRow: (item: any) => ({
      ...item,
      status:
        item.status === true || item.status === "true" || item.status === "Active"
          ? "Active"
          : "Inactive",
      specialtySearchText: Array.isArray(item.specialty)
        ? item.specialty.map((specialty: any) => specialty.name).join(", ")
        : "",
    }),
    sortRows: (rows) =>
      [...rows]
        .sort((a: any, b: any) => Number(b.id) - Number(a.id))
        .sort((a: any, b: any) =>
          a.is_deleted === b.is_deleted ? 0 : a.is_deleted ? 1 : -1
        ),
    onError: (error) => {
      console.error("Error fetching items:", error);
      toast({ title: "Error", description: "Failed to fetch Doctors", variant: "destructive" });
    },
  });

  // ─── Status Toggle — with Force Dialog support ────────────────────────────
  const toggleDoctorStatus = async (item: Result, force = false): Promise<void> => {
    const currentStatus = item.status === "Active" || item.status === true;
    try {
      const ValuesSpecialty = (item.specialty || []).map((s: any) => s.id ?? s);
      const url = force ? "updateDoctor?force=true" : "updateDoctor";
      const response = await DoctorAxiosInstance.put(url, {
        data: {
          Doctor_name: item.doctor_name,
          Doctor_id: item.doctor_id,
          specialty: ValuesSpecialty,
          Status: !currentStatus,
          id: item.id,
        },
        token,
      });

      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false);
        setForceId(null);
        setForceTargetItem(null);
        setForceError("");
        setForceAction(null);
        toast({ title: "Success", description: `Status changed to ${!currentStatus ? "Active" : "Inactive"}.` });
      } else {
        setForceId(item.id);
        setForceTargetItem(item);
        setForceAction("status");
        setForceError(response.data.error || "This doctor is used elsewhere.");
        setForceDialogOpen(true);
      }
    } catch (error: any) {
      const errMsg = error.response?.data?.error || "";
      if (error.response?.status === 409 || errMsg.toLowerCase().includes("used") || errMsg.toLowerCase().includes("referenced")) {
        setForceId(item.id);
        setForceTargetItem(item);
        setForceAction("status");
        setForceError(errMsg || "This doctor is used elsewhere.");
        setForceDialogOpen(true);
      } else {
        toast({ title: "Error", description: errMsg || "An unexpected error occurred.", variant: "destructive" });
      }
    }
  };

  const addItem = async (): Promise<void> => {
    setNameError(""); setIdError(""); setSpecialtyError("");
    if (!Name.trim() || !doctorId.trim()) {
      if (!Name.trim()) setNameError("Name required");
      if (!doctorId.trim()) setIdError("ID required");
      toast({ title: "Error", description: "Name and Doctor ID are required.", variant: "destructive" });
    }
    if (Name.trim().length < 3) {
      setNameError("Name must be at least 3 characters long");
      toast({ title: "Error", description: "Name must be at least 3 characters long.", variant: "destructive" });
    }
    if (specialty.length > 1) {
      setSpecialtyError("A doctor can only be assigned one specialty.");
      toast({ title: "Error", description: "A doctor can only have one specialty.", variant: "destructive" });
      return;
    }
    if (nameError || idError || Name.trim().length < 3 || !doctorId.trim()) return;
    try {
      const ValuesSpecialty = specialty.map((option) => option.value);
      const payload = { data: [{ Doctor_name: Name, Doctor_id: doctorId, specialty: ValuesSpecialty, Status: isActive }], token };
      const response = await DoctorAxiosInstance.post("addNewDoctor", payload);
      if (response.data.success) {
        await fetchItems();
        setCurrentPage(0);
        clear();
        setIsDialogOpen(false);
        toast({ title: "Success", description: "New Doctor Added Successfully." });
      } else {
        toast({ title: "Error", description: response.data.error || "Failed to add Doctor.", variant: "destructive" });
      }
    } catch (error: AxiosError | any) {
      toast({ title: "Error", description: error.response?.data?.error || "An unexpected error occurred.", variant: "destructive" });
    }
  };

  const handleEdit = (item: Result) => {
    setNameError(""); setIdError(""); setSpecialtyError("");
    setName(item.doctor_name);
    setDoctorId(item.doctor_id);
    const specialtyData = Array.isArray(item.specialty)
      ? item.specialty.map((obj) => typeof obj === "string" ? { id: 0, name: obj } : obj)
      : [];
    const limitedSpecialtyData = specialtyData.slice(0, 1);
    setSpecialty(limitedSpecialtyData.map((obj) => ({ value: obj.id, label: obj.name })));
    setIsActive(item.status === "Active" || item.status === true);
    setEditIndex(item.id);
  };

  const updateItem = async (id: string | number): Promise<void> => {
    if (!Name.trim() || !doctorId.trim()) {
      toast({ title: "Error", description: "Name and Doctor ID are required.", variant: "destructive" });
      return;
    }
    if (Name.trim().length < 3) {
      toast({ title: "Error", description: "Name must be at least 3 characters long.", variant: "destructive" });
      return;
    }
    if (specialty.length > 1) {
      setSpecialtyError("A doctor can only be assigned one specialty.");
      toast({ title: "Error", description: "A doctor can only have one specialty.", variant: "destructive" });
      return;
    }
    const ValuesSpecialty = specialty.map((option) => option.value);
    try {
      const payload = { data: { Doctor_name: Name, Doctor_id: doctorId, specialty: ValuesSpecialty, Status: isActive, id }, token };
      const response = await DoctorAxiosInstance.put("updateDoctor", payload);
      if (response.data.success) {
        await fetchItems();
        setCurrentPage(0);
        clear();
        setIsDialogOpen(false);
        toast({ title: "Success", description: "Doctor updated successfully." });
      }
    } catch (error: AxiosError | any) {
      toast({ title: "Error", description: error.response?.data?.error || "An unexpected error occurred.", variant: "destructive" });
    }
  };

  // ─── Soft Delete ──────────────────────────────────────────────────────────
  const removeItem = async (id: string | number): Promise<void> => {
    try {
      const response = await DoctorAxiosInstance.delete(`softDeleteDoctor/${id}`, { data: { token } });
      if (response.data.success) {
        await fetchItems();
        toast({ title: "Success", description: "Doctor Deleted Successfully.", variant: "success" });
      } else {
        setForceId(id);
        setForceAction("softDelete");
        setForceError(response.data.error || "This doctor is used elsewhere.");
        setForceDialogOpen(true);
      }
    } catch (error: any) {
      const errMsg = error.response?.data?.error || "";
      if (error.response?.status === 409 || errMsg.toLowerCase().includes("used") || errMsg.toLowerCase().includes("referenced")) {
        setForceId(id); setForceAction("softDelete"); setForceError(errMsg); setForceDialogOpen(true);
      } else {
        toast({ title: "Error", description: errMsg || "Failed to delete Doctor", variant: "destructive" });
      }
    }
  };

  // ─── Restore ──────────────────────────────────────────────────────────────
  const restoreItem = async (id: string | number): Promise<void> => {
    try {
      const response = await DoctorAxiosInstance.patch(`restoreDoctor/${id}`, { token });
      if (response.data.success) {
        await fetchItems();
        toast({ title: "Success", description: "Doctor restored successfully." });
      } else {
        toast({ title: "Error", description: response.data.error || "Failed to restore Doctor", variant: "destructive" });
      }
    } catch (error: any) {
      toast({ title: "Error", description: error.response?.data?.error || "Failed to restore Doctor", variant: "destructive" });
    }
  };

  // ─── Hard Delete (permanent) ──────────────────────────────────────────────
  const permanentDeleteItem = async (id: string | number, force = false): Promise<void> => {
    try {
      const url = force ? `deleteDoctor/${id}?force=true` : `deleteDoctor/${id}`;
      const response = await DoctorAxiosInstance.delete(url, { data: { token } });
      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null);
        toast({ title: "Success", description: "Doctor Permanently Deleted." });
      } else {
        setForceAction("hardDelete");
        setForceError(response.data.error || "This doctor is used elsewhere.");
      }
    } catch (error: any) {
      setForceAction("hardDelete");
      setForceError(error.response?.data?.error || "Failed to delete Doctor");
    }
  };

  // ─── Force Soft Delete ────────────────────────────────────────────────────
  const forceSoftDelete = async (id: string | number): Promise<void> => {
    try {
      const response = await DoctorAxiosInstance.delete(`softDeleteDoctor/${id}?force=true`, { data: { token } });
      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null);
        toast({ title: "Success", description: "Doctor force Deleted Successfully." });
      } else {
        setForceError(response.data.error || "Failed to force delete Doctor");
      }
    } catch (error: any) {
      setForceError(error.response?.data?.error || "Failed to force delete Doctor");
    }
  };

  const handleDownload = (item: Result) => {
    const rows = ["name;doctor_id;specialty;is_active"];
    const Specialty = item.specialty || [];
    const maxLen = Math.max(1, Specialty.length);
    for (let index = 0; index < maxLen; index++) {
      const spec = index < Specialty.length ? Specialty[index].name || "" : "";
      rows.push([item.doctor_name || "", item.doctor_id || "", spec, item.status ? "TRUE" : "FALSE"].join(";"));
    }
    const csvContent = rows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `Doctor${item.id}.csv`; a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleOpenDialog = (item: Result) => { setSelectedItem(item); dialogRef.current?.showModal(); };
  const handleCloseDialog = () => { setSelectedItem(null); setShowAllUpdates(false); };

  const columns: ColumnDef<Result>[] = [
    {
      accessorKey: "id",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full text-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          ID{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => (
        <span className="block w-full text-center py-3 font-medium">{row.original.id}</span>
      ),
    },
    {
      accessorKey: "doctor_name",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full text-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Doctor Name{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const isDeleted = (row.original as any).is_deleted;
        return (
          <span className={`block w-full text-center py-3 ${isDeleted ? "line-through text-gray-400" : ""}`}>
            {row.getValue("doctor_name")}
          </span>
        );
      },
    },
    {
      accessorKey: "doctor_id",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full text-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Doctor ID{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => (
        <span className="block w-full text-center py-3">{row.getValue("doctor_id")}</span>
      ),
    },
    {
      accessorKey: "specialty",
      accessorFn: (row) => {
        if (!Array.isArray(row.specialty) || row.specialty.length === 0) return "";
        return row.specialty.map((s) => s.name).join(", ");
      },
      header: ({ column }) => (
        <Button variant="ghost" className="w-full text-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Specialty{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const specialtyData = row.original.specialty as { id: number; name: string }[];
        if (!Array.isArray(specialtyData) || specialtyData.length === 0) {
          return <span className="text-gray-400 block text-center">-</span>;
        }
        return (
          <div className="flex flex-wrap justify-center items-center gap-1.5">
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
              {specialtyData[0].name}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: "status",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full text-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Status{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        const isActiveRow = item.status === "Active" || item.status === true;
        const isDeleted = (item as any).is_deleted;
        return (
          <div className="flex justify-center">
            <button
              onClick={() => !isDeleted && toggleDoctorStatus(item)}
              disabled={!!isDeleted}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                isDeleted ? "opacity-40 cursor-not-allowed bg-gray-200" : isActiveRow ? "bg-green-500" : "bg-gray-300"
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                isActiveRow && !isDeleted ? "translate-x-6" : "translate-x-1"
              }`} />
            </button>
          </div>
        );
      },
    },
    {
      id: "view",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full text-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          View{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className="w-full flex justify-center items-center gap-2 py-2">
            <button onClick={() => setSelectedItem(item)} className="text-blue-600 hover:text-blue-800" title="View Details">
              <i className="fa-solid fa-eye text-lg"></i>
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="h5-container mx-auto px-1 pt-16 pb-4">
      <div className="flex justify-between items-center">
        <h5 className="mb-6 flex items-center gap-2 font-bold text-2xl mt-4 text-blue-900">
          <span className="icon-container flex items-center justify-center w-10 h-10 rounded-full bg-blue-900 text-white">
            <i className="fa-solid fa-user-md"></i>
          </span>
          Doctor
        </h5>
      </div>
      <DataTable2
        columns={columns}
        data={items.result}
        searchColumns={[]}
        searchValue={filterName}
        onSearchChange={setFilterName}
        searchPlaceholder="Search..."
        searchHelpText="Doctor ID, Doctor Name, or Specialty."
        addButtonLabel="Add New Doctor"
        showAddButton={true}
        compactToolbar={true}
        toolbarUpload={<FileUpload fetchItems={fetchItems} />}
        onEdit={handleEdit}
        onDelete={(id) => {
          const item = items.result.find(i => i.id === id);
          if ((item as any)?.is_deleted) {
            setForceId(id);
            setForceAction("hardDelete");
            setForceError("");
            setForceDialogOpen(true);
          } else {
            removeItem(id);
          }
        }}
        onRestore={canManageDeleted ? restoreItem : undefined}
        canHardDelete={canManageDeleted}
        onAdd={handleAddClick}
        onEditClick={handleEditClick}
        pageIndex={currentPage}
        onPageChange={setCurrentPage}
        manualPagination={manualPagination}
        pageCount={totalPages}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageSizeChange={(nextPageSize) => {
          setPageSize(nextPageSize);
          setCurrentPage(0);
        }}
        allowedPageSizes={allowedPageSizes}
        isLoading={isTableLoading}
        onDownload={handleDownload}
        isRowDeleted={(row: any) => row.is_deleted === true}
        toolbarExtras={
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <ServerStatusPills
              value={filterStatus}
              onChange={(value) => {
                setFilterStatus(value);
                setCurrentPage(0);
              }}
            />
            <ServerDeletedPills
              value={filterDeleted}
              onChange={(value) => {
                setFilterDeleted(value);
                setCurrentPage(0);
              }}
            />
          </div>
        }
      />

      {/* Specialty Dialog */}
      <Dialog open={specialtyDialogOpen} onOpenChange={setSpecialtyDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-blue-900 flex items-center gap-2">
              <i className="fa-solid fa-list-check"></i>All Specialties
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
              <div className="grid grid-cols-1 gap-2 max-h-96 overflow-y-auto pr-2">
                {selectedSpecialties.map((specialty, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm border border-blue-100 hover:border-blue-300 hover:shadow-md transition-all">
                    <div className="flex items-center justify-center w-8 h-8 bg-blue-600 text-white rounded-full flex-shrink-0">
                      <i className="fa-solid fa-user-doctor text-sm"></i>
                    </div>
                    <span className="text-gray-800 font-medium flex-1">{specialty.name}</span>
                    <span className="text-xs text-gray-500 bg-blue-100 px-2 py-1 rounded">#{index + 1}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-3 border-t border-blue-200">
                <p className="text-sm text-gray-600 flex items-center gap-2">
                  <i className="fa-solid fa-info-circle text-blue-600"></i>
                  <span>Total: <strong className="text-blue-900">{selectedSpecialties.length}</strong> specialties</span>
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setSpecialtyDialogOpen(false)} className="bg-blue-600 hover:bg-blue-700">
              <i className="fa-solid fa-check mr-2"></i>Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Details Dialog */}
      {selectedItem && (
        <Dialog open={!!selectedItem} onOpenChange={(open) => { if (!open) setSelectedItem(null); }}>
          <DialogContent className="max-w-[780px] max-h-[92vh] flex flex-col p-0 overflow-hidden bg-white border-none rounded-2xl" style={{ boxShadow: "0 25px 60px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)" }}>

            {/* Header */}
            <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 60%, #2563eb 100%)", padding: "24px 28px 20px", flexShrink: 0, position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", top: -40, right: -40, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
              <div style={{ position: "absolute", bottom: -30, left: -20, width: 100, height: 100, borderRadius: "50%", background: "rgba(255,255,255,0.03)" }} />
              <div style={{ display: "flex", alignItems: "flex-start", gap: 16, position: "relative", zIndex: 1 }}>
                <div style={{ width: 48, height: 48, borderRadius: 14, background: "rgba(255,255,255,0.12)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, color: "#fff", flexShrink: 0, border: "1px solid rgba(255,255,255,0.1)" }}>
                  <i className="fa-solid fa-user-md" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 2, flexWrap: "wrap" }}>
                    <h2 style={{ fontSize: 20, fontWeight: 700, color: "#fff", margin: 0, letterSpacing: "-0.01em" }}>{selectedItem.doctor_name}</h2>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 12px", borderRadius: 9999, fontSize: 11, fontWeight: 600, flexShrink: 0, background: (selectedItem.status === "Active" || selectedItem.status === true) ? "rgba(34,197,94,0.2)" : "rgba(239,68,68,0.15)", color: (selectedItem.status === "Active" || selectedItem.status === true) ? "#86efac" : "#fca5a5", border: `1px solid ${(selectedItem.status === "Active" || selectedItem.status === true) ? "rgba(34,197,94,0.25)" : "rgba(239,68,68,0.2)"}` }}>
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} />
                      {selectedItem.status === "Active" || selectedItem.status === true ? "Active" : "Inactive"}
                    </span>
                    {(selectedItem as any).is_deleted && (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 12px", borderRadius: 9999, fontSize: 11, fontWeight: 600, background: "rgba(239,68,68,0.15)", color: "#fca5a5", border: "1px solid rgba(239,68,68,0.2)" }}>
                        <i className="fa-solid fa-trash" style={{ fontSize: 10 }} /> Deleted
                      </span>
                    )}
                  </div>
                  <p style={{ fontSize: 12, color: "rgba(147,197,253,0.8)", margin: 0 }}>Doctor id: <span style={{ fontWeight: 600, color: "#93c5fd" }}>#{selectedItem.doctor_id}</span></p>
                </div>
              </div>
            </div>

            {/* Body */}
            <ScrollArea className="flex-1" style={{ background: "#f8fafc" }}>
              <div style={{ padding: "20px 28px 16px" }}>

                {/* Basic Info */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #dbeafe" }}>
                    <i className="fa-solid fa-circle-info" style={{ fontSize: 12, color: "#3b82f6" }} />
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Basic information</span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }}>
                  {[
                    { label: "Full name", value: selectedItem.doctor_name, icon: "fa-user-md", color: "#3b82f6" },
                    { label: "Doctor id", value: `#${selectedItem.doctor_id}`, icon: "fa-id-card", color: "#8b5cf6" },
                  ].map((field) => (
                    <div key={field.label} style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                        <div style={{ width: 22, height: 22, borderRadius: 6, background: `${field.color}12`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <i className={`fa-solid ${field.icon}`} style={{ fontSize: 10, color: field.color }} />
                        </div>
                        <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "capitalize", letterSpacing: "0.05em" }}>{field.label}</span>
                      </div>
                      <span style={{ fontSize: 14, fontWeight: 600, color: "#1e293b" }}>{field.value || "—"}</span>
                    </div>
                  ))}
                </div>

                <div style={{ height: 1, background: "#e2e8f0", margin: "0 0 20px" }} />

                {/* Specialty */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #dbeafe" }}>
                    <i className="fa-solid fa-stethoscope" style={{ fontSize: 12, color: "#3b82f6" }} />
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Specialty</span>
                </div>
                <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", marginBottom: 20, boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                  {selectedItem.specialty && selectedItem.specialty.length > 0 ? (
                    <span style={{ display: "inline-flex", alignItems: "center", padding: "4px 14px", borderRadius: 9999, fontSize: 12, fontWeight: 500, background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe" }}>
                      {selectedItem.specialty[0].name}
                    </span>
                  ) : (
                    <span style={{ color: "#94a3b8", fontSize: 12, fontStyle: "italic" }}>No specialty assigned</span>
                  )}
                </div>

                <div style={{ height: 1, background: "#e2e8f0", margin: "0 0 20px" }} />

                {/* Audit Trail */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #bbf7d0" }}>
                    <i className="fa-solid fa-clock-rotate-left" style={{ fontSize: 12, color: "#16a34a" }} />
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Audit trail</span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                  <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Created by</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
                      <div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg, #3b82f6, #2563eb)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
                        {String(resolveUserName(selectedItem.created_by) ?? "?")[0].toUpperCase()}
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{resolveUserName(selectedItem.created_by)}</span>
                    </div>
                  </div>
                  <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Created on</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                      <i className="fa-regular fa-calendar" style={{ fontSize: 14, color: "#3b82f6" }} />
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                        {selectedItem.createdat ? new Date(selectedItem.createdat).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                      </span>
                    </div>
                  </div>
                  <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Last updated</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                      <i className="fa-regular fa-clock" style={{ fontSize: 14, color: "#10b981" }} />
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                        {selectedItem.updatedat ? new Date(selectedItem.updatedat).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                      </span>
                    </div>
                  </div>
                </div>

                {Array.isArray(selectedItem.updated_by) && selectedItem.updated_by.length > 0 && (
                  <div style={{ gridColumn: "1 / -1", background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)", marginTop: 12 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
                      <i className="fa-solid fa-pen-to-square" style={{ fontSize: 11, color: "#10b981" }} />
                      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.02em" }}>Update History</span>
                      <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#f0fdf4", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0", marginLeft: "auto" }}>
                        {selectedItem.updated_by.length} update{selectedItem.updated_by.length !== 1 ? "s" : ""}
                      </span>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {(selectedItem.updated_by as { id?: number; at?: string }[]).slice().reverse().slice(0, showAllUpdates ? undefined : 4).map((entry, i) => (
                        <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 8, background: i === 0 ? "#f0fdf4" : "#f8fafc", border: `1px solid ${i === 0 ? "#bbf7d0" : "#e2e8f0"}` }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div style={{ width: 28, height: 28, borderRadius: "50%", background: i === 0 ? "linear-gradient(135deg, #10b981, #059669)" : "linear-gradient(135deg, #94a3b8, #64748b)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
                              {String(resolveUserName(entry.id) ?? "?")[0].toUpperCase()}
                            </div>
                            <span style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{resolveUserName(entry.id)}</span>
                            {i === 0 && <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#dcfce7", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0" }}>Latest</span>}
                          </div>
                          <span style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>
                            {entry.at ? new Date(entry.at).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                          </span>
                        </div>
                      ))}
                      {selectedItem.updated_by.length > 4 && (
                        <div style={{ textAlign: "center", padding: "6px 0" }}>
                          <button onClick={() => setShowAllUpdates(v => !v)} style={{ fontSize: 11, color: "#3b82f6", fontWeight: 600, background: "none", border: "none", cursor: "pointer" }}>
                            {showAllUpdates ? "Show less ↑" : `+${selectedItem.updated_by.length - 4} more updates ↓`}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div style={{ paddingBottom: 8 }} />
              </div>
            </ScrollArea>

            {/* Footer */}
            <div style={{ padding: "14px 28px", borderTop: "1px solid #e2e8f0", background: "#fff", display: "flex", alignItems: "center", justifyContent: "flex-end", flexShrink: 0 }}>
              <Button onClick={() => setSelectedItem(null)} style={{ background: "linear-gradient(135deg, #1e3a8a, #2563eb)", color: "#fff", fontWeight: 600, padding: "8px 28px", borderRadius: 10, boxShadow: "0 2px 8px rgba(37,99,235,0.3)" }}>
                Close Details
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent
          ref={setDialogContentEl}
          onInteractOutside={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          className="max-w-[640px] max-h-[90vh] flex flex-col p-0 bg-white border-none rounded-2xl"
          style={{ boxShadow: "0 25px 60px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)", overflow: "visible" }}
        >
          {/* Header */}
          <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 60%, #2563eb 100%)", padding: "22px 28px", position: "relative", overflow: "hidden", borderRadius: "16px 16px 0 0", flexShrink: 0 }}>
            <div style={{ position: "absolute", top: -40, right: -40, width: 140, height: 140, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
            <div style={{ display: "flex", alignItems: "center", gap: 14, position: "relative", zIndex: 1 }}>
              <div style={{ width: 42, height: 42, borderRadius: 12, background: "rgba(255,255,255,0.12)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, color: "#fff", flexShrink: 0, border: "1px solid rgba(255,255,255,0.1)" }}>
                <i className={`fa-solid ${editIndex === null ? "fa-plus" : "fa-pen"}`} />
              </div>
              <div>
                <h2 style={{ fontSize: 18, fontWeight: 700, color: "#fff", margin: 0 }}>
                  {editIndex === null ? "Add Doctor" : "Edit Doctor"}
                </h2>
                </div>
            </div>
          </div>

          <ScrollArea className="flex-1" style={{ background: "#f8fafc" }}>
            <div style={{ padding: "22px 28px 32px" }}>

              {/* Basic Information */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <div style={{ width: 26, height: 26, borderRadius: 7, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #dbeafe" }}>
                  <i className="fa-solid fa-circle-info" style={{ fontSize: 11, color: "#3b82f6" }} />
                </div>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Basic Information</span>
              </div>

              <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "18px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)", marginBottom: 18 }}>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name" className="text-xs font-semibold text-slate-500">Doctor Name</Label>
                    <Input id="name" value={Name} onChange={handleNameChange} placeholder="Name is required" className={`mt-1.5 ${nameError ? "border-red-400 focus-visible:ring-red-300" : ""}`} />
                    {nameError && <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1"><i className="fa-solid fa-circle-exclamation text-[10px]" />{nameError}</p>}
                  </div>
                  <div>
                    <Label htmlFor="doctor_id" className="text-xs font-semibold text-slate-500">Doctor ID</Label>
                    <Input id="doctor_id" value={doctorId} onChange={handleIdChange} placeholder="Doctor ID is required" className={`mt-1.5 ${idError ? "border-red-400 focus-visible:ring-red-300" : ""}`} />
                    {idError && <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1"><i className="fa-solid fa-circle-exclamation text-[10px]" />{idError}</p>}
                  </div>
                </div>
              </div>

              {/* Specialty & Status */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <div style={{ width: 26, height: 26, borderRadius: 7, background: "#faf5ff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #e9d5ff" }}>
                  <i className="fa-solid fa-stethoscope" style={{ fontSize: 11, color: "#9333ea" }} />
                </div>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Specialty & Status</span>
              </div>

              <div style={{ background: "#fff", borderRadius: 12, border: `1px solid ${specialtyError ? "#fca5a5" : "#e2e8f0"}`, padding: "18px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                <Label className="text-xs font-semibold text-slate-500">
                  Specialty <span className="text-slate-400 font-normal">(one only)</span>
                </Label>
                <div className="mt-1.5">
                  <SpecialtySelector onChange={handleSpecialtyChange} clear={clearSpecialty} value={specialty} portalTarget={dialogContentEl} />
                </div>
                {specialtyError && <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1"><i className="fa-solid fa-circle-exclamation text-[10px]" />{specialtyError}</p>}

                <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid #e2e8f0" }}>
                  <Label className="text-xs font-semibold text-slate-500">Status</Label>
                  <div style={{ display: "flex", gap: 8, marginTop: 8, maxWidth: 260 }}>
                    <button
                      type="button"
                      onClick={() => setIsActive(true)}
                      style={{
                        flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "8px 0", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer",
                        border: isActive ? "1px solid #16a34a" : "1px solid #e2e8f0",
                        background: isActive ? "#f0fdf4" : "#fff",
                        color: isActive ? "#15803d" : "#64748b",
                        transition: "all 0.15s",
                      }}
                    >
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} /> Active
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsActive(false)}
                      style={{
                        flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "8px 0", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer",
                        border: !isActive ? "1px solid #dc2626" : "1px solid #e2e8f0",
                        background: !isActive ? "#fef2f2" : "#fff",
                        color: !isActive ? "#b91c1c" : "#64748b",
                        transition: "all 0.15s",
                      }}
                    >
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} /> Inactive
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </ScrollArea>

          {/* Footer */}
          <div style={{ padding: "16px 28px", borderTop: "1px solid #e2e8f0", background: "#fff", display: "flex", justifyContent: "flex-end", gap: 10, borderRadius: "0 0 16px 16px", flexShrink: 0 }}>
            <Button variant="outline" onClick={() => { clear(); setEditIndex(null); setIsDialogOpen(false); }} style={{ borderRadius: 10, fontWeight: 600 }}>
              Cancel
            </Button>
            <Button
              onClick={() => { if (editIndex === null) addItem(); else updateItem(editIndex); }}
              style={{ background: "linear-gradient(135deg, #1e3a8a, #2563eb)", color: "#fff", fontWeight: 600, padding: "8px 24px", borderRadius: 10, boxShadow: "0 2px 8px rgba(37,99,235,0.3)" }}
            >
              <i className={`fa-solid ${editIndex === null ? "fa-plus" : "fa-check"} mr-2`}></i>
              {editIndex === null ? "Add Doctor" : "Update Doctor"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── Force / Hard Delete Dialog ───────────────────────────────────────── */}
      {forceDialogOpen && (
        <div style={{ position: "fixed", inset: 0, background: "#00000088", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(4px)" }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: 32, width: 440, boxShadow: "0 20px 50px rgba(0,0,0,0.3)", border: "1px solid #fecaca" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 12 }}>
              <div style={{ width: 60, height: 60, borderRadius: "50%", background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i className="fa-solid fa-triangle-exclamation" style={{ fontSize: 28, color: "#dc2626" }}></i>
              </div>

              {forceAction === "hardDelete" && (
                <>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", margin: 0 }}>Permanent Delete</h3>
                  <p style={{ fontSize: 13, color: "#64748b", margin: 0, lineHeight: 1.6 }}>
                    This will <strong style={{ color: "#dc2626" }}>permanently delete</strong> the doctor.<br />This action <strong>cannot be undone</strong>.
                  </p>
                  {forceError && (
                    <div style={{ width: "100%", background: "#fef9c3", border: "1px solid #fde047", borderRadius: 10, padding: "12px 16px", textAlign: "left" }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                        <i className="fa-solid fa-circle-info" style={{ color: "#ca8a04", marginTop: 2, flexShrink: 0 }}></i>
                        <p style={{ fontSize: 13, color: "#713f12", margin: 0, lineHeight: 1.6 }}>{forceError}</p>
                      </div>
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 10, width: "100%", marginTop: 4 }}>
                    <button
                      onClick={() => { setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null); }}
                      style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
                      Cancel
                    </button>
                    <button
                      onClick={() => forceId !== null && permanentDeleteItem(forceId, true)}
                      style={{ flex: 1, background: "#7f1d1d", color: "#fff", border: "none", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
                      <i className="fa-solid fa-bolt mr-2"></i>Force Delete Anyway
                    </button>
                  </div>
                </>
              )}

              {(forceAction === "softDelete" || forceAction === "status") && (
                <>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", margin: 0 }}>
                    {forceAction === "status" ? "Status Change Blocked" : "Doctor Is In Use"}
                  </h3>
                  <div style={{ width: "100%", background: "#fef9c3", border: "1px solid #fde047", borderRadius: 10, padding: "12px 16px", textAlign: "left" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                      <i className="fa-solid fa-circle-info" style={{ color: "#ca8a04", marginTop: 2, flexShrink: 0 }}></i>
                      <p style={{ fontSize: 13, color: "#713f12", margin: 0, lineHeight: 1.6 }}>{forceError}</p>
                    </div>
                  </div>
                  <p style={{ fontSize: 13, color: "#64748b", margin: 0 }}>
                    Do you want to <strong style={{ color: "#dc2626" }}>force {forceAction === "status" ? "update status" : "delete"}</strong> anyway?
                  </p>
                  <div style={{ display: "flex", gap: 10, width: "100%", marginTop: 4 }}>
                    <button onClick={() => { setForceDialogOpen(false); setForceId(null); setForceTargetItem(null); setForceError(""); setForceAction(null); }}
                      style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>Cancel</button>
                    <button onClick={() => {
                        if (!forceId) return;
                        if (forceAction === "softDelete") forceSoftDelete(forceId);
                        else if (forceAction === "status" && forceTargetItem) toggleDoctorStatus(forceTargetItem, true);
                      }}
                      style={{ flex: 1, background: "#dc2626", color: "#fff", border: "none", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
                      <i className="fa-solid fa-bolt" style={{ marginRight: 8 }}></i>
                      Force {forceAction === "status" ? "Update" : "Delete"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <Toaster />
    </div>
  );
};

export default Doctor;